/**
 * Use shop route content.
 *
 * Explains routing behavior, guards, and response semantics.
 */
import { useEffect, useState } from 'react'
import { loadRouteContentData } from './shopRuntimeApi'
import type { CmsPage as CmsContent } from '../../cms/types'
import type { CatalogProduct, CategoryRouteData, Route } from '../types'
import {
  applyLoadedRouteContent,
  applyRouteContentLoadError,
  handleStaticRouteMode,
  prepareDynamicRouteLoading,
} from './useShopRouteContent.helpers'

type Params = {
  route: Route
  onProductResolved: (product: CatalogProduct) => void
}

/**
 * Use shop route content.
 *
 * Explains routing behavior, guards, and response semantics.
 */
export const useShopRouteContent = ({ route, onProductResolved }: Params) => {
  const [pageData, setPageData] = useState<CmsContent | null>(null)
  const [pageLoading, setPageLoading] = useState(false)
  const [pageError, setPageError] = useState('')
  const [productData, setProductData] = useState<CatalogProduct | null>(null)
  const [productLoading, setProductLoading] = useState(false)
  const [productError, setProductError] = useState('')
  const [categoryRouteData, setCategoryRouteData] = useState<CategoryRouteData | null>(null)

  useEffect(() => {
    const setters = {
      setPageData,
      setPageError,
      setProductData,
      setProductError,
      setCategoryRouteData,
      setPageLoading,
      setProductLoading,
    }
    if (handleStaticRouteMode({ route, setters })) {
      return
    }

    const controller = new AbortController()
    const loadContent = async () => {
      prepareDynamicRouteLoading({ route, setters })

      try {
        const content = await loadRouteContentData(route, controller.signal)
        applyLoadedRouteContent({
          content,
          onProductResolved,
          setters,
        })
      } catch (error) {
        if (controller.signal.aborted) return
        applyRouteContentLoadError({ error, route, setters })
      } finally {
        if (!controller.signal.aborted) {
          setPageLoading(false)
          setProductLoading(false)
        }
      }
    }

    void loadContent()
    return () => controller.abort()
  }, [onProductResolved, route])

  return {
    pageData,
    pageLoading,
    pageError,
    productData,
    productLoading,
    productError,
    categoryRouteData,
  }
}
